import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserPlus, Wallet, Megaphone, Library, Inbox, AlertTriangle, CheckCircle2, XCircle, Clock, Radio, Music2 } from "lucide-react";
import { PageHeader, StatCard, Panel, Loading, StatusBadge } from "@/components/common";
import { api } from "@/lib/api";
import { brl, fmtDate, fmtDateTime, fmtDur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const SERVICES = [["storage", "Armazenamento de áudio"], ["tts_provisional", "Voz provisória (OpenAI)"], ["voice_clone", "Clonagem de voz (ElevenLabs)"], ["mercadopago", "Mercado Pago"], ["webhook_secret", "Webhook Mercado Pago"], ["email", "E-mail de notificações"]];

const Service = ({ ok, label }) => (
  <li className="flex items-center justify-between py-2 text-sm">
    <span>{label}</span>
    {ok ? <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-4 h-4" />Ativo</span> : <span className="flex items-center gap-1 text-amber-300"><XCircle className="w-4 h-4" />Não configurado</span>}
  </li>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [live, setLive] = useState({ clients: [], online: 0, users_online_total: 0, updated_at: null });
  const [liveError, setLiveError] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data));
  }, []);

  useEffect(() => {
    let active = true;
    const loadLive = () => {
      api.get("/admin/radio/online")
        .then((r) => {
          if (active) {
            setLive(r.data);
            setLiveError(false);
          }
        })
        .catch(() => {
          if (active) setLiveError(true);
        });
    };

    loadLive();
    const timer = window.setInterval(loadLive, 10000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);
  if (!d) return <Loading />;
  const s = d.subscriptions;
  return (
    <div className="space-y-8">
      <PageHeader title="Dashboard" subtitle={`Bem-vindo, ${user.name}. Visão geral da operação MarkImagem Rádio Web.`} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-clients-active" label="Clientes ativos" value={d.clients_active} icon={Users} />
        <StatCard testid="stat-clients-new" label="Novos (30 dias)" value={d.clients_new_30d} icon={UserPlus} />
        {d.mrr !== undefined && <StatCard testid="stat-mrr" accent label="Receita recorrente (MRR)" value={brl(d.mrr)} icon={Wallet} />}
        <StatCard testid="stat-requests-pending" label="Solicitações aguardando" value={d.requests_pending} icon={Inbox} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-subs-active" label="Assinaturas ativas" value={s.ativa + s.teste} hint={`${s.teste} em teste`} />
        <StatCard testid="stat-subs-pending" label="Pendentes" value={s.pagamento_pendente} />
        <StatCard testid="stat-subs-overdue" label="Vencidas / inadimplentes" value={s.inadimplente} />
        <StatCard testid="stat-subs-cancelled" label="Canceladas" value={s.cancelada + s.cancelamento_solicitado} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-ads-active" label="Anúncios no ar" value={d.ads.em_veiculacao} hint={`${d.ads.agendado} agendados`} icon={Megaphone} />
        <StatCard testid="stat-ads-pending" label="Anúncios pendentes" value={d.ads.aguardando_aprovacao + d.ads.rascunho} icon={Clock} />
        <StatCard testid="stat-ads-expired" label="Anúncios expirados" value={d.ads.expirado} icon={AlertTriangle} />
        <StatCard testid="stat-tracks" label="Músicas cadastradas" value={d.tracks_total} hint={`${d.tracks_active} ativas • playlist ${d.playlist ? fmtDur(d.playlist.total_duration) : "—"}`} icon={Library} />
      </div>
      {d.payments_month && (
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard testid="stat-payments-approved" label="Pagamentos aprovados (mês)" value={d.payments_month.aprovado} />
          <StatCard testid="stat-payments-refused" label="Pagamentos recusados (mês)" value={d.payments_month.recusado} />
          <StatCard testid="stat-payments-pending" label="Pagamentos pendentes (mês)" value={d.payments_month.pendente} />
        </div>
      )}
      <Panel
        title="Clientes online e reprodução ao vivo"
        action={<span className="text-xs text-slate-500">Atualização automática a cada 10 segundos</span>}
        testid="live-clients-panel"
      >
        <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
          <span className="inline-flex items-center gap-2 text-emerald-400 font-semibold">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            {live.online} {live.online === 1 ? "cliente online" : "clientes online"}
          </span>
          <span className="text-slate-500">{live.users_online_total} {live.users_online_total === 1 ? "usuário conectado" : "usuários conectados"}</span>
          <span className="text-xs text-slate-500 ml-auto">
            {live.updated_at ? `Atualizado ${fmtDateTime(live.updated_at)}` : "Aguardando dados..."}
          </span>
        </div>
        {liveError && <p className="text-xs text-amber-300 mb-3">Não foi possível atualizar o monitor agora. Uma nova tentativa será feita automaticamente.</p>}
        {live.clients.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-slate-500" data-testid="live-clients-empty">
            Nenhum cliente conectado neste momento.
            <span className="block text-xs mt-1">O cliente aparece aqui enquanto mantém o painel aberto.</span>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="live-clients-list">
            {live.clients.map((client) => {
              const label = client.connection === "buffering"
                ? "Carregando áudio"
                : client.connection === "reconnecting"
                  ? "Reconectando"
                  : client.playing
                    ? "Reproduzindo"
                    : client.connection === "connecting"
                      ? "Conectando"
                      : client.connection === "error"
                        ? "Rádio indisponível"
                        : "Online • player parado";
              return (
                <article key={client.business_id} className="rounded-xl border border-border bg-background/40 p-4 space-y-3" data-testid={`live-client-${client.business_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{client.business_name}</p>
                      <p className="text-xs text-slate-500 mt-1">{client.users_online} {client.users_online === 1 ? "usuário conectado" : "usuários conectados"}</p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online
                    </span>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg bg-secondary/40 p-3">
                    {client.playing && client.current ? (
                      <>
                        <Music2 className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs uppercase tracking-wide text-slate-500">{client.current.type === "ad" ? "Anúncio em reprodução" : "Música em reprodução"}</p>
                            <span className="text-[10px] rounded-full px-2 py-0.5 bg-emerald-500/10 text-emerald-300">
                              {client.connection === "buffering" ? "Carregando" : client.connection === "reconnecting" ? "Reconectando" : client.connection === "connecting" ? "Conectando" : "Ao vivo"}
                            </span>
                          </div>
                          <p className="font-medium text-sm break-words mt-1">{client.current.title}</p>
                          {client.current.artist && <p className="text-xs text-slate-400 mt-0.5">{client.current.artist}</p>}
                          {client.current.started_at && <p className="text-[11px] text-slate-500 mt-1">Início: {fmtDateTime(client.current.started_at)}</p>}
                        </div>
                      </>
                    ) : (
                      <>
                        <Radio className="w-5 h-5 mt-0.5 shrink-0 text-slate-500" />
                        <div>
                          <p className="text-sm font-medium">{label}</p>
                          <p className="text-xs text-slate-500 mt-0.5">Nenhum conteúdo sendo informado como reproduzido.</p>
                        </div>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500">Último contato: {fmtDateTime(client.last_seen)}</p>
                </article>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-4">Um cliente é considerado online enquanto o painel envia um sinal a cada 15 segundos. Sem sinal por 90 segundos, ele deixa de aparecer.</p>
      </Panel>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Status dos serviços" testid="services-status-panel">
          <ul className="divide-y divide-border">{SERVICES.map(([k, l]) => <Service key={k} ok={d.services[k]} label={l} />)}</ul>
          <p className="text-xs text-slate-500 mt-3">Falhas de streaming nas últimas 24h: {d.stream_failures_24h}</p>
        </Panel>
        <Panel title="Anúncios vencendo em até 3 dias" action={<Link to="/admin/anuncios" className="text-xs text-primary">Ver todos</Link>}>
          {d.expiring_ads.length === 0 ? <p className="text-sm text-slate-500">Nenhum anúncio próximo do vencimento.</p> : (
            <ul className="divide-y divide-border" data-testid="expiring-ads-list">
              {d.expiring_ads.map((a) => <li key={a.id} className="py-2 text-sm flex justify-between gap-2"><span className="truncate">{a.title}<span className="block text-xs text-slate-500">{a.business_name}</span></span><span className="text-amber-300 shrink-0">{fmtDate(a.end_date)}</span></li>)}
            </ul>
          )}
        </Panel>
        <Panel title="Atividades recentes">
          <ul className="divide-y divide-border text-sm" data-testid="recent-activity-list">
            {d.recent_activity.map((l) => <li key={l.id} className="py-2"><p><span className="font-mono text-xs text-accent">{l.action}</span> <span className="text-slate-400">{l.entity}</span></p><p className="text-xs text-slate-500">{l.user_email} • {fmtDateTime(l.created_at)}</p></li>)}
          </ul>
        </Panel>
      </div>
      <p className="text-xs text-slate-500">Status: <StatusBadge status="ativa" /> = OK. Os indicadores são calculados em tempo real a partir do banco de dados.</p>
    </div>
  );
}
